/**
 * ResendEmailButton Component
 * ===========================
 * Button to resend assignment notification email with cooldown support.
 */

import { useState, useEffect } from 'react'
import { assignmentService } from '../../services'

interface ResendEmailButtonProps {
  emailSentAt: string | null
  clientEmail: string | null
  onResend: () => Promise<void>
  disabled?: boolean
  compact?: boolean
}

export function ResendEmailButton({
  emailSentAt,
  clientEmail,
  onResend,
  disabled = false,
  compact = false,
}: ResendEmailButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const canResend = assignmentService.canResendEmail(emailSentAt)
  const minutesUntilResend = assignmentService.getMinutesUntilResend(emailSentAt)

  // Update countdown every 30 seconds when in cooldown
  useEffect(() => {
    if (!canResend && minutesUntilResend > 0) {
      setCountdown(minutesUntilResend)

      const interval = setInterval(() => {
        const remaining = assignmentService.getMinutesUntilResend(emailSentAt)
        setCountdown(remaining)
        if (remaining <= 0) {
          clearInterval(interval)
        }
      }, 30000) // Update every 30 seconds

      return () => clearInterval(interval)
    } else {
      setCountdown(0)
    }
  }, [emailSentAt, canResend, minutesUntilResend])

  const handleClick = async () => {
    if (!canResend || !clientEmail || disabled || isLoading) return
    setIsLoading(true)
    try {
      await onResend()
    } finally {
      setIsLoading(false)
    }
  }

  // No email address - show disabled text
  if (!clientEmail) {
    return (
      <span className="text-xs text-gray-400" title="Client has no email address">
        No email
      </span>
    )
  }

  // In cooldown - show countdown
  if (!canResend && countdown > 0) {
    return (
      <span className="text-xs text-gray-500" title="Email recently sent">
        Resend in {countdown}m
      </span>
    )
  }

  // Ready to resend
  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={disabled || isLoading}
        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title={emailSentAt ? 'Resend email' : 'Send email'}
      >
        {isLoading ? (
          <svg
            className="animate-spin w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        )}
      </button>
    )
  }

  return (
    <button
      onClick={handleClick}
      disabled={disabled || isLoading}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      title={emailSentAt ? 'Resend assignment email' : 'Send assignment email'}
    >
      {isLoading ? (
        <svg
          className="animate-spin w-3.5 h-3.5"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      ) : (
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      )}
      {emailSentAt ? 'Resend' : 'Send Email'}
    </button>
  )
}
